import { RoleId } from '@common/constants';
import { ALL_PROVINCES, type Province } from '@common/enums';
import type { LawFirm } from '@common/types/lawfirms';
import { formatConveyancers } from '@common/utils/conveyancers';
import { formatFreeTrialPracticeAreas } from '@common/utils/freeTrial';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
    Button,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
} from '@mui/material';
import { useMemo, useState } from 'react';
import DataTable, { type Column } from '@/components/DataTable';
import PageWrapper from '@/components/PageWrapper';
import NotFound from '@/pages/NotFound';
import { useLawFirmList } from '@/queries/lawFirms';
import useIdentityStore from '@/stores/useIdentityStore';
import { formatBillingDate } from '@/utils/format';
import CreateOrUpdateLawFirmDialog from './CreateOrUpdateLawFirmDialog';

const FORM_ITEM_STYLES = {
    backgroundColor: '#F4F6F8',
    color: '#7B7D80',
};

function formatCurrency(value: number | string | null | undefined) {
    if (value == null) return '';

    return new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: 'CAD',
    }).format(Number(value));
}

function formatBoolean(value: boolean | undefined) {
    return value ? 'Yes' : 'No';
}

export default function LawFirms() {
    const loggedInUser = useIdentityStore((state) => state.user);
    const isSuperAdmin = !!loggedInUser?.roles?.some((role) => role.roleId === RoleId.SUPER_ADMIN);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchKey, setSearchKey] = useState('');
    const [selectedProvince, setSelectedProvince] = useState<Province | ''>('');
    const [isLawFirmDialogOpen, setIsLawFirmDialogOpen] = useState(false);
    const [selectedLawFirm, setSelectedLawFirm] = useState<LawFirm | null>(null);

    const { data: lawFirms = [], isLoading } = useLawFirmList(!!loggedInUser && isSuperAdmin);

    const filteredLawFirms = useMemo(() => {
        const normalizedSearchKey = searchKey.trim().toLowerCase();
        return lawFirms.filter((lawFirm) => {
            if (selectedProvince && lawFirm.province !== selectedProvince) return false;
            if (!normalizedSearchKey) return true;

            return lawFirm.companyName?.toLowerCase().includes(normalizedSearchKey);
        });
    }, [lawFirms, searchKey, selectedProvince]);

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const columns: Column<LawFirm>[] = [
        { id: 'companyName', label: 'Law Firm', _isSortable: true },
        { id: 'province', label: 'Province', _isSortable: true },
        {
            id: 'conveyancers',
            label: 'Conveyancers',
            _render: (lawFirm) => formatConveyancers(lawFirm.conveyancers),
            _isSortable: false,
        },
        {
            id: 'stripeCustomerId',
            label: 'Stripe ID',
            _render: (lawFirm) => formatBoolean(!!lawFirm.stripeCustomerId),
            _isSortable: true,
        },
        {
            id: 'isTestFirm',
            label: 'Test Firm',
            _render: (lawFirm) => formatBoolean(lawFirm.isTestFirm),
            _isSortable: false,
        },
        {
            id: 'isPrivate',
            label: 'Private',
            _render: (lawFirm) => formatBoolean(lawFirm.isPrivate),
            _isSortable: false,
        },
        {
            id: 'isFreeTrial',
            label: 'Free Trial',
            _render: (lawFirm) => formatFreeTrialPracticeAreas(lawFirm),
            _isSortable: false,
        },
        {
            id: 'quoteIntakeBasePrice',
            label: 'Quote & Intake Price',
            align: 'right' as const,
            _render: (lawFirm) => formatCurrency(lawFirm.quoteIntakeBasePrice),
            _isSortable: false,
        },
        {
            id: 'intakeOnlyBasePrice',
            label: 'Intake Price',
            align: 'right' as const,
            _render: (lawFirm) => formatCurrency(lawFirm.intakeOnlyBasePrice),
            _isSortable: false,
        },
        {
            id: 'idvBasePrice',
            label: 'IDV Price',
            align: 'right' as const,
            _render: (lawFirm) => formatCurrency(lawFirm.idvBasePrice),
            _isSortable: false,
        },
        {
            id: 'createdAt',
            label: 'Created',
            _render: (lawFirm) => formatBillingDate(lawFirm.createdAt),
            _isSortable: true,
        },
        {
            id: 'edit',
            label: 'Edit',
            _render: (lawFirm) => (
                <IconButton
                    style={{ height: '40px', width: '40px' }}
                    onClick={() => {
                        setSelectedLawFirm(lawFirm);
                        setIsLawFirmDialogOpen(true);
                    }}
                >
                    <EditOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
            ),
            _isSortable: false,
        },
    ];

    if (loggedInUser && !isSuperAdmin) {
        return <NotFound />;
    }

    return (
        <PageWrapper
            title="Law Firms"
            actions={
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        setSelectedLawFirm(null);
                        setIsLawFirmDialogOpen(true);
                    }}
                >
                    Create Law Firm
                </Button>
            }
        >
            <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                    value={searchKey}
                    onChange={(ev) => {
                        setSearchKey(ev.target.value);
                        setPage(0);
                    }}
                    sx={{ ...FORM_ITEM_STYLES, width: '400px' }}
                    placeholder="Search law firms by name"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <FormControl sx={{ ...FORM_ITEM_STYLES, minWidth: '220px' }}>
                    <InputLabel id="law-firm-province-filter">Province</InputLabel>
                    <Select
                        labelId="law-firm-province-filter"
                        label="Province"
                        value={selectedProvince}
                        onChange={(ev) => {
                            setSelectedProvince(ev.target.value as Province | '');
                            setPage(0);
                        }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {ALL_PROVINCES.map((province) => (
                            <MenuItem key={province} value={province}>
                                {province}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>
            <DataTable
                generateKeyFrom="lawFirmId"
                isLoading={isLoading}
                columns={columns}
                rows={filteredLawFirms}
                page={page}
                rowsPerPage={rowsPerPage}
                handleChangePage={(_ev, newPage) => setPage(newPage)}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
            {isLawFirmDialogOpen && (
                <CreateOrUpdateLawFirmDialog
                    lawFirm={selectedLawFirm ?? undefined}
                    onClose={() => {
                        setSelectedLawFirm(null);
                        setIsLawFirmDialogOpen(false);
                    }}
                />
            )}
        </PageWrapper>
    );
}
